<template>
  <main>
    <Trail
      :list="[
        {
          path: '/proyectos/investigacion',
          name: 'Projects',
          tag: 'Proyectos de Investigación'
        },
        {
          name: 'PROCLIM',
          tag: 'PROCLIM'
        }
      ]"
    />
    <Container class="container--main">
      <Title tag="h1" align="center">
        Programa de Fortalecimiento de Capacidades Nacionales para manejar el
        Impacto del Cambio Climático y la Contaminación del Aire (PROCLIM)
      </Title>
      <section>
        <Title tag="h4" align="center" theme="secondary">
          Subproyecto "Vulnerabilidad y Adaptación al Cambio Climático en la
          Cuenca del río Mantaro"
        </Title>
        <Figure height="250px">
          <Image
            fullSize
            :srcsets="['/images/proyectos/proclim/agric.jpg']"
            title="Agricultores en el Valle del Mantaro"
          />
          <template #figcaption>
            Agricultores en el Valle del Mantaro
          </template>
        </Figure>
      </section>
      <Row>
        <ColumnUi md="9">
          <Navtab id="introduccion">
            <Title tag="h2">Introducción</Title>
            <p>
              El cambio climático que se ha observado en el último siglo, una de
              cuyos efectos más claros es el aumento en la temperatura promedio
              del planeta, está asociado a cambios en el clima a escala regional
              y local que dependerán en gran parte de los detalles de la
              geografía a esta escala. El caso de los valles interandinos es
              particularmente problemático debido a lo compleja de la orografía
              que los rodea, la cual puede tener grandes impactos en el clima
              local. Si bien existen aspectos robustos del cambio climático,
              como el derretimiento de los glaciares, los cambios en las
              precipitaciones lluviosas son mucho más variables de lugar en
              lugar.
            </p>
            <p>
              Considerando lo anterior, es evidente que desde la perspectiva de
              la población y de sus actividades que es necesario realizar una
              evaluación del cambio climático y de sus impactos a escala local
              para poder planificar de la mejor manera las medidas que deberán
              ser tomadas para la adaptación al cambio climático.
            </p>
            <p>
              Una metodología que ha emergido como una de las más útiles para
              enfrentar el problema es la
              <a
                href="http://en.wikipedia.org/wiki/Integrated_assessment_modelling"
                target="_blank"
                >"evaluación integrada"</a
              >, la cual es una actividad multidisciplinaria para investigar los
              efectos del cambio climático en diversos aspectos de la sociedad y
              medio ambiente y la relación entre ellos. La "Evaluación Local
              Integrada" (ELI) es realizada en un ámbito geográfico limitado, lo
              cual ignora como primera aproximación las interacciones con otras
              regiones.
            </p>
          </Navtab>
          <Navtab id="evaluacion">
            <Title tag="h2">
              Evaluación Local Integrada de la Cuenca del Río Mantaro
            </Title>
            <p>
              La cuenca del río Mantaro, en los Andes centrales del Perú, es de
              gran importancia para el país debido a que concentra la mayor
              población andina, es la principal región agrícola, y sus centrales
              hidroelécticas son las que proveen mayor energía a escala
              nacional.
            </p>
            <p>
              El Instituto Geofísico del Perú (IGP), en coordinación con lo que
              fue el Consejo Nacional del Ambiente (CONAM) y dentro del Programa
              de Fortalecimiento de Capacidades Nacionales para Manejar el
              Impacto del Cambio Climático y la Contaminación del Aire
              (PROCLIM), desarrolló el estudio "Evaluación Local Integrada de la
              Cuenca del río Mantaro", como parte de las actividades del
              Subprograma "Vulnerabilidad y Adaptación al Cambio Climático en la
              Cuenca del río Mantaro". El PROCLIM contó con el financiamiento
              del Gobierno de los Países Bajos.
            </p>
            <p>
              Los objetivos centrales de la ELI Mantaro fueron:
            </p>
            <List marked>
              <li>
                Ampliar el conocimiento sobre la variabilidad y cambio climático
                en la Cuenca del río Mantaro,
              </li>
              <li>
                Evaluar los aspectos físicos y sociales de su vulnerabilidad
                ante el cambio, e
              </li>
              <li>
                Identificar opciones viables de adaptación en los principales
                sectores socio-económicos de la región, para que puedan ser
                incorporados en los planes de desarrollo local y regional.
              </li>
            </List>
          </Navtab>
          <Navtab id="resultados">
            <Title tag="h2">
              Resultados
            </Title>
            <Figure right="sm" maxWidth="400px">
              <Image
                linked
                :srcsets="['/images/proyectos/proclim/pp_ANUAL_1.gif']"
                title="Promedio multianual de precipitación (mm) en la cuenca del Mantaro"
              />
              <template #figcaption>
                Promedio multianual de precipitación (mm) en la cuenca del
                Mantaro
              </template>
            </Figure>
            <p>
              En dicho proceso, se logró un amplio respaldo y participación de
              las principales instituciones u organismos gubernamentales y no
              gubernamentales en la cuenca.
            </p>
            <p>
              Se elaboró una serie de tres documentos con los principales
              resultados obtenidos:
            </p>
            <List tag="ol">
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/714"
                  target="_blank"
                >
                  "Atlas Climático de precipitación y temperaturas del aire en
                  la cuenca"
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/715"
                  target="_blank"
                >
                  "Diagnóstico de la Cuenca bajo la visión del cambio climático"
                </a>
              </li>
              <li>
                <a
                  href="https://repositorio.igp.gob.pe/handle/20.500.12816/742"
                  target="_blank"
                >
                  "Vulnerabilidad actual y futura ante el cambio climático, y
                  medidas de adaptación en la cuenca"
                </a>
              </li>
            </List>

            <p>
              Un paso importante en el estudio fue el diseño, levantamiento de
              información y elaboración del atlas y diagnóstico, que sirvieron
              de línea base en el conocimiento de la cuenca - que abarca
              territorios de las regiones Junín, Pasco, Huancavelica y Ayacucho
              -, ofreciendo un panorama integral de la realidad, no sólo
              climática, biofísica y socioeconómica, sino también, cultural y de
              conocimientos ancestrales y actuales de la realidad ambiental, tal
              como lo percibe la población.
            </p>
            <p>
              El atlas climático contiene principalmente mapas de la
              distribución espacial de los promedios multianuales de las lluvias
              y temperaturas del aire (medias, máximas y mínimas), para cada mes
              y estación del año.
            </p>
            <p>
              En lo referido a los escenarios climáticos futuros estimados para
              la cuenca del río Mantaro, el downscaling estadístico y dinámico,
              herramientas matemáticas para regionalizar los escenarios
              climáticos globales, estimó para el año 2050: un aumento de 1,3°C
              y 1g/kg en temperatura y humedad específica, respectivamente, y
              una disminución de 6% en la humedad relativa en la cuenca durante
              los meses lluviosos, de diciembre a febrero. Asimismo, las
              precipitaciones disminuirían en 10%, 19% y 14% en las zonas norte,
              centro y sur de la cuenca, respectivamente.
            </p>
            <Figure maxWidth="300px" left="sm">
              <Image
                linked
                :srcsets="[
                  '/images/proyectos/proclim/tendESpacial_PPen_anual.gif'
                ]"
                title="Tendencias observadas en precipitación anual (1964-2003). Reducción e incremento en rojo y azul, respectivamente."
              />
              <template #figcaption>
                Tendencias observadas en precipitación anual (1964-2003).
                Reducción e incremento en rojo y azul, respectivamente.
              </template>
            </Figure>
            <p>
              Las implicancias de estas estimaciones serían bastante graves. Las
              variaciones de temperatura del aire y la irregularidad en las
              precipitaciones afectarían sobre todo a los sectores de
              agricultura, generación de energía hidroeléctrica y salud.
            </p>
            <p>
              Indudablemente el agua, en su uso, distribución y gestión, es el
              factor transversal en el estudio, por ello, su manejo integral con
              el concurso de todos los actores involucrados, ha sido considerado
              vital en la adopción de medidas de adaptación que permitan
              aumentar la resilencia de la población de la cuenca ante cualquier
              cambio.
            </p>

            <p>
              Acciones tales como el ordenamiento territorial de la cuenca, y la
              distribución de la población creciente, son parte de la línea base
              de gestión necesaria que debe ser tomada en cuenta para que las
              medidas de adaptación puedan ser apropiadamente incorporadas en
              los planes de desarrollo local y regional, las cuales deben ir
              acompañadas del fortalecimiento institucional y el concurso y
              acción, de todos los actores involucrados en la cuenca.
            </p>
            <p>
              Las propuestas fueron logradas dentro un proceso participativo,
              que ulteriormente contribuyó en la creación del primer Grupo
              Técnico Regional sobre Cambio Climático, Adaptación y
              Vulnerabilidad a nivel nacional, en el gobierno regional de
              Junín<sup>1</sup>, así como el primer documento de Estrategia
              Regional al Cambio Climático, también consolidada para la región
              Junín<sup>2</sup>.
            </p>
          </Navtab>
          <Navtab id="productos">
            <Title tag="h2">
              Productos
            </Title>
            <p>
              En el contexto de este subproyecto, se realizaron tres
              publicaciones con los resultados. Los documentos, junto con
              versiones de alta resolución de los mapas en ellos y los archivos
              para sistemas de información geográfica (SIG) correspondiente a la
              información producida por el IGP en este subproyecto se pueden
              obtener de los enlaces a continuación:
            </p>
            <TableUi>
              <tbody>
                <tr>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/714"
                      target="_blank"
                    >
                      Atlas Climático de precipitación y temperaturas del aire
                      en la cuenca
                    </a>
                  </td>
                  <td>
                    <a
                      :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/MAPAS-VOLI.rar`"
                      target="_blank"
                    >
                      Mapas
                    </a>
                  </td>
                  <td>
                    <a :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/SIG-VOLI.rar`"
                    target="_blank">
                      SIG
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/715"
                      target="_blank"
                    >
                      Diagnóstico de la Cuenca bajo la visión del cambio
                      climático
                    </a>
                  </td>
                  <td>
                    <a
                      :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/MAPAS-VOLII.rar`"
                      target="_blank"
                    >
                      Mapas
                    </a>
                  </td>
                  <td>
                    <a :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/MAPAS-VOLII.rar`"
                    target="_blank">
                      SIG
                    </a>
                  </td>
                </tr>
                <tr>
                  <td>
                    <a
                      href="https://repositorio.igp.gob.pe/handle/20.500.12816/742"
                      target="_blank"
                    >
                      Vulnerabilidad actual y futura ante el cambio climático, y
                      medidas de adaptación en la cuenca
                    </a>
                  </td>
                  <td>
                    <a
                      :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/MAPAS-VOLIII.rar`"
                      target="_blank"
                    >
                      Mapas
                    </a>
                  </td>
                  <td>
                    <a
                      :href="`https://www.igp.gob.pe/programas-de-investigacion/ciencias-de-la-atmosfera-e-hidrosfera/resources/proyectos/proclim/SIG-VOLIII.rar`"
                      target="_blank"
                    >
                      SIG
                    </a>
                  </td>
                </tr>
              </tbody>
            </TableUi>
          </Navtab>
        </ColumnUi>
        <ColumnUi md="3">
          <Nav :items="navItems" title="Contenido" />
        </ColumnUi>
      </Row>

      <br />
      <hr />
      <List tag="ol">
        <li>
          Resolución Ejecutiva Regional (RER) N° 244-2005 GRJ/PR del 17 de junio
          del 2005
        </li>
        <li>
          Decreto Regional N° 002-2007-GRJ/PR del 04 de diciembre del 2007
        </li>
      </List>
    </Container>
  </main>
</template>
<script>
import Trail from "@/components/common/Trail";
import Title from "@/components/ui/atoms/Title";
import List from "@/components/ui/objects/List";
import Container from "@/components/ui/objects/Container";
import Figure from "@/components/ui/objects/Figure";
import Image from "@/components/ui/objects/Image";
import Row from "@/components/ui/atoms/Row";
import ColumnUi from "@/components/ui/atoms/ColumnUi";
import Nav from "@/components/ui/objects/Nav";
import Navtab from "@/components/ui/objects/Navtab";
import TableUi from "@/components/ui/objects/TableUi";
export default {
  components: {
    Trail,
    Title,
    List,
    Container,
    Figure,
    Image,
    Row,
    ColumnUi,
    Nav,
    Navtab,
    TableUi
  },
  setup() {
    const navItems = [
      {
        path: "#introduccion",
        tag: "Introducción"
      },
      {
        path: "#evaluacion",
        tag: "Evaluación Local Integrada de la Cuenca del Río Mantaro"
      },
      {
        path: "#resultados",
        tag: "Resultados"
      },
      {
        path: "#productos",
        tag: "Productos"
      }
    ];
    return {
      navItems
    };
  }
};
</script>
